import {
  useEffect,
  useRef,
  useCallback,
  RefObject,
  useState,
  UIEventHandler,
  ReactElement,
  Children,
  cloneElement,
  useMemo,
} from 'react';
import {
  Drawer,
  useTheme,
  useMediaQuery,
  Box,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BottomSheet } from 'react-spring-bottom-sheet';
import {
  RefHandles,
  SpringEvent,
  defaultSnapProps,
} from 'react-spring-bottom-sheet/dist/types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IosAppSwitcherHandler } from 'components/IosAppSwitcherHandler';
import { useDrawerSnapPointState } from 'state/useDrawerSnapPointState';
import { Capacitor } from '@capacitor/core';
import { useScrollPositionState } from 'state/useScrollPositionState';
import { useDeviceOrientation } from 'hooks/useScreenOrientation';
import { usePrevious } from 'hooks/usePrevious';
import { useDebounceCallback } from 'usehooks-ts';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { useRegisterMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';
import CloseIcon from '@mui/icons-material/Close';
import { OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX } from 'features/mapping/mapping.constants';
import { useLocationState } from 'hooks/useLocationState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { pickMapSearchParams } from 'features/mapping/utils/pickMapSearchParams';
import {
  getSearchCardMinSnapPx,
  MAP_DOM_ELEMENT_ID,
  DOWNLOAD_APP_BANNER_OFFSET_CSS_VALUE,
  TOOLBAR_HEIGHT_DESKTOP_PX,
} from '../../../../constants';
import {
  MAP_ENTITY_DRAWER_WIDTH,
  MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID,
} from './MapEntityDrawer.constants';
import {
  DesktopScrollProvider,
  MobileScrollProvider,
} from './ScrollContextProviders';

type MapEntityDrawerProps = {
  children: ReactElement;
  open?: boolean;
  snapPointsPct: number[];
  defaultSnapPointPct: number;
  entityId?: string | number;
  onClose?: () => void;
  dismissible?: boolean;
  preserveDrawerHeight?: boolean;
  closeRedirectPath?: string;
  preventDrawerClose?: boolean;
};

type WithCustomKeyProps = {
  children: ReactElement;
  customKey: string | number;
};

type SnapTarget = number | 'minimize' | 'restore';

type LatestSheetRef = {
  isBottomSheetOpening: boolean;
  minSnapPct: number;
  defaultSnapPointPct: number;
};

const useStyles = makeStyles()((theme) => ({
  drawerPaper: {
    paddingTop: `calc(${TOOLBAR_HEIGHT_DESKTOP_PX}px + env(safe-area-inset-top) + ${DOWNLOAD_APP_BANNER_OFFSET_CSS_VALUE})`,
    paddingBottom: `env(safe-area-inset-bottom)`,
    width: MAP_ENTITY_DRAWER_WIDTH,
    pointerEvents: 'auto',
  },
  mobileHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  card: {
    borderRadius: 0,
    minHeight: '100%',
    position: 'relative',
    width: '100%',
    zIndex: theme.zIndex.drawer - 1,
    [theme.breakpoints.up(660)]: {
      paddingTop: theme.spacing(2),
    },
  },
}));

const resolveTargetPct = (
  target: SnapTarget,
  { minSnapPct, defaultSnapPointPct }: LatestSheetRef,
): number => {
  if (target === 'minimize') return minSnapPct;
  if (target === 'restore') return defaultSnapPointPct;
  return target;
};

const WithCustomKey = (props: WithCustomKeyProps): ReactElement => {
  const { children, customKey } = props;
  // Ensure only one child at runtime
  const onlyChild = Children.only(children);
  /**
   * Clone the single child with a custom `key`.
   * - Changing the key forces React to remount the component,
   *   which resets its internal state.
   * - Useful when switching between entity drawers of the same type,
   *   so that content (e.g., sticky headers with dynamic content) is re-initialized.
   */
  return cloneElement(onlyChild, {
    key: customKey,
  });
};

export const MapEntityDrawer = (props: MapEntityDrawerProps): JSX.Element => {
  const {
    children,
    open = false,
    snapPointsPct,
    defaultSnapPointPct,
    entityId = 'entityId', // Any value as default
    onClose,
    dismissible = true,
    preserveDrawerHeight = true,
    closeRedirectPath,
    preventDrawerClose = false,
  } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const sheetRef = useRef<RefHandles | null>(null);
  const scrollElementRef = useRef<Element>();
  const { drawerHeight, updateDrawerSnapPoint } =
    useDrawerSnapPointState('map-entity-drawer');
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { open: openState, updateDrawerState } = useMapEntityDrawerState();
  // Bottom sheet state tracking to ensure programmatic snap point changes aren't ignored during the opening animation.
  const [isBottomSheetOpening, setIsBottomSheetOpening] = useState(false);

  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const { fromRoute } = useLocationState('/');

  if (snapPointsPct.length < 1) {
    throw new Error('snapPointsPct must have at least one element');
  }
  if (!snapPointsPct.includes(defaultSnapPointPct)) {
    throw new Error(
      `defaultSnapPointPct (${defaultSnapPointPct}) must be in snapPointsPct (${snapPointsPct})`,
    );
  }

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') {
      return () => {};
    }

    const statusTapHandler = (): void => {
      if (scrollElementRef.current) {
        scrollElementRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('statusTap', statusTapHandler);

    return () => {
      window.removeEventListener('statusTap', statusTapHandler);
    };
  }, []);

  useEffect(() => {
    // Ensure scroll resets to top on consecutive drawer openings.
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [entityId]);

  const minSnapPct = snapPointsPct[0];
  // Holds a pending snap target when executeSnapTo is called while the bottom
  // sheet is still opening or the sheet ref isn't ready yet.
  const snapToFrameRef = useRef<number>();
  const pendingSnapRef = useRef<SnapTarget | null>(null);
  // Holds the latest prop values so imperative callbacks always read fresh data
  // without needing to be recreated when props change. Updated synchronously
  // during render so it's always current by the time any callback fires.
  const latestRef = useRef<LatestSheetRef>({
    isBottomSheetOpening,
    minSnapPct,
    defaultSnapPointPct,
  });
  latestRef.current = { isBottomSheetOpening, minSnapPct, defaultSnapPointPct };

  useEffect(() => {
    return () => {
      // Cleanup any pending animation frames on unmount
      if (snapToFrameRef.current) {
        cancelAnimationFrame(snapToFrameRef.current);
      }
    };
  }, []);

  const executeSnapTo = useCallback((target: SnapTarget, tolerance = 10) => {
    // Defer the snap until the opening animation completes
    if (latestRef.current.isBottomSheetOpening) {
      pendingSnapRef.current = target;
      return;
    }

    // Cancel any previously scheduled frame to avoid redundant snaps
    if (snapToFrameRef.current) {
      cancelAnimationFrame(snapToFrameRef.current);
    }

    // Sheet not mounted yet, snap not possible
    if (!sheetRef.current) {
      return;
    }

    snapToFrameRef.current = requestAnimationFrame(() => {
      if (!sheetRef.current) return;

      // Named targets are resolved here, inside the animation frame callback,
      // so they always read from the latest ref values, preventing stale prop
      // issues when props change between the call and the frame.
      const targetPct = resolveTargetPct(target, latestRef.current);

      const targetSnapPoint = Math.round(window.innerHeight * targetPct);
      const currentHeight = sheetRef.current.height;

      // Skip if already at or within tolerance of the target position
      if (Math.abs(currentHeight - targetSnapPoint) <= tolerance) return;

      sheetRef.current.snapTo(targetSnapPoint);
    });
  }, []);

  // Flush any pending snap once the opening animation finishes
  useEffect(() => {
    if (!isBottomSheetOpening && pendingSnapRef.current !== null) {
      const targetPct = pendingSnapRef.current;
      pendingSnapRef.current = null;
      executeSnapTo(targetPct);
    }
  }, [isBottomSheetOpening, executeSnapTo]);

  const minimizeEntityDrawer = useCallback((): void => {
    executeSnapTo('minimize');
  }, [executeSnapTo]);

  const restoreEntityDrawer = useCallback((): void => {
    executeSnapTo('restore');
  }, [executeSnapTo]);

  const snapEntityDrawerTo = useCallback(
    (snapPointPct: number): void => {
      executeSnapTo(snapPointPct);
    },
    [executeSnapTo],
  );

  const methods = useMemo(() => {
    return { minimizeEntityDrawer, restoreEntityDrawer, snapEntityDrawerTo };
  }, [restoreEntityDrawer, minimizeEntityDrawer, snapEntityDrawerTo]);
  useRegisterMapEntityDrawer(methods);

  // We manage the open state using global state to mitigate a UX issue:
  // Closing the map entity drawer triggers a route re-render, interrupting its closing animation.
  // To prevent this, we synchronize the open state with a global state and defer the route
  // change until the animation completes (see `handleClose` below).
  // This ensures a smoother user experience, allowing the drawer to finish its closing animation
  // before navigation. This is particularly important for mobile devices, where the bottom sheet's
  // closing animation is longer.
  useEffect(() => {
    updateDrawerState({ open, minSnapPct });
  }, [minSnapPct, open, updateDrawerState]);

  useEffect(() => {
    return () => {
      // Reset drawer state on unmount to prevent persistence issues
      // Ensures drawer state doesn't interfere with other pages using this component
      // Specifically fixes map control positioning when navigating between pages
      updateDrawerState({ open: false });
    };
  }, [updateDrawerState]);

  const {
    scrollPosition,
    setScrollPosition,
    reset: resetScrollState,
  } = useScrollPositionState(
    `geo-event-drawer-${history.location.pathname + history.location.hash}`,
  );
  const deviceOrientation = useDeviceOrientation();
  const prevDeviceOrientation = usePrevious(deviceOrientation);
  const debouncedRestoreScrollPosition = useDebounceCallback(
    useCallback((position: number) => {
      if (!scrollElementRef.current) return;
      scrollElementRef.current.scrollTop = position;
    }, []),
    100,
  );
  // Restore scroll position when device orientation changes,
  useEffect(() => {
    if (
      prevDeviceOrientation &&
      !['any', 'natural'].includes(prevDeviceOrientation) &&
      prevDeviceOrientation !== deviceOrientation &&
      scrollElementRef.current &&
      scrollPosition
    ) {
      debouncedRestoreScrollPosition(scrollPosition);
    }
  }, [
    debouncedRestoreScrollPosition,
    deviceOrientation,
    prevDeviceOrientation,
    scrollPosition,
  ]);

  const debouncedSetScrollPosition = useDebounceCallback(
    setScrollPosition,
    500,
  );

  const handleScroll: UIEventHandler<HTMLDivElement> = useCallback(
    (event): void => {
      const element = event.target as HTMLDivElement;
      if (!element) return;
      debouncedSetScrollPosition(element.scrollTop);
    },
    [debouncedSetScrollPosition],
  );

  const { clearOverlappingFeatures, hasOverlappingFeatures } =
    useOverlappingFeaturesState();

  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClose = (): void => {
    updateDrawerSnapPoint(0);
    if (scrollPosition) resetScrollState();
    onClose?.();
    clearOverlappingFeatures();

    if (!preventDrawerClose) updateDrawerState({ open: false });

    // @todo always use the `fromRoute` fallback when removing 'pin_drop_improvements' release flag
    const [redirectPathname, search = pickMapSearchParams(location.search)] =
      pinImprovements
        ? (closeRedirectPath || fromRoute).split('?')
        : [closeRedirectPath ?? '/'];

    // Redirecting to the drawer's own route would leave it mounted but closed.
    const pathname =
      pinImprovements && redirectPathname === location.pathname
        ? '/'
        : redirectPathname;

    // closing animation for the bottom drawer takes longer, so ensure that the animation has finished before we re-route
    timeoutRef.current = setTimeout(
      () =>
        history.push({
          pathname,
          search,
        }),
      isLargeMediaQuery ? 50 : 350,
    );
  };

  const handleSpringStart = (event: SpringEvent): void => {
    if (event.type === 'OPEN') {
      setIsBottomSheetOpening(true);
    }

    const bottomSheetScrollElement = document.querySelector(
      `#${MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID} [data-rsbs-scroll]`,
    );

    if (bottomSheetScrollElement) {
      scrollElementRef.current = bottomSheetScrollElement;
    }
  };

  const handleSpringEnd = (event: SpringEvent): void => {
    if (event.type === 'OPEN') {
      setIsBottomSheetOpening(false);
    }

    if (!sheetRef.current || !scrollElementRef.current) return;

    if (event.type === 'SNAP' && event.source === 'dragging') {
      updateDrawerSnapPoint(sheetRef.current.height);
    }

    if (event.type === 'SNAP' && event.source !== 'dragging') return;

    // Attempt to scroll to the top if the user drags the drawer to the minimum snap point
    const minSnapPoint = Math.round(window.innerHeight * minSnapPct);

    // Do nothing if the drawer is not at the minimum snap point
    if (sheetRef.current.height - 5 > minSnapPoint) return;

    scrollElementRef.current.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const getDefaultSnapPoint = useCallback(
    ({ maxHeight }: defaultSnapProps): number => {
      if (!preserveDrawerHeight) {
        return Math.round(maxHeight * defaultSnapPointPct);
      }
      return Math.max(
        drawerHeight,
        Math.round(maxHeight * defaultSnapPointPct),
      );
    },
    [defaultSnapPointPct, drawerHeight, preserveDrawerHeight],
  );

  if (isLargeMediaQuery) {
    return (
      <Drawer
        sx={(th) => ({ pointerEvents: 'none', zIndex: th.zIndex.drawer + 1 })}
        classes={{ paper: classes.drawerPaper }}
        anchor="right"
        open={openState}
        onClose={dismissible ? handleClose : undefined}
        PaperProps={{
          'aria-label': t('geoEvent.details'),
        }}
        hideBackdrop
        container={() => document.getElementById(MAP_DOM_ELEMENT_ID)}
      >
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            // Add padding to the bottom to account for the overlapping features control height
            pb: hasOverlappingFeatures
              ? `${OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX}px`
              : undefined,
          }}
        >
          {dismissible && (
            <IconButton
              aria-label={t('common.closePanel')}
              onClick={handleClose}
              color="inherit"
              sx={{
                position: 'absolute',
                top: theme.spacing(0.75),
                right: theme.spacing(1),
                zIndex: theme.zIndex.drawer,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <Card
            className={classes.card}
            sx={{
              overflow: 'auto',
              maxHeight: '100%',
              bgcolor: 'background.paper',
            }}
            ref={scrollElementRef as RefObject<HTMLDivElement>}
            onScrollCapture={handleScroll}
            elevation={0}
          >
            <CardContent id="mapdrawercardcontent" sx={{ padding: 0 }}>
              <DesktopScrollProvider
                scrollElementRef={scrollElementRef}
                closeDrawer={handleClose}
              >
                <WithCustomKey customKey={entityId}>{children}</WithCustomKey>
              </DesktopScrollProvider>
            </CardContent>
          </Card>
        </Box>
      </Drawer>
    );
  }

  return (
    <BottomSheet
      id={MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID}
      open={openState}
      onDismiss={dismissible ? handleClose : undefined}
      skipInitialTransition
      ref={sheetRef}
      onScrollCapture={handleScroll}
      sibling={<IosAppSwitcherHandler />}
      snapPoints={({ maxHeight }) =>
        snapPointsPct.map((pct) => Math.round(maxHeight * pct))
      }
      defaultSnap={getDefaultSnapPoint}
      expandOnContentDrag
      blocking={false}
      header={
        <div className={classes.mobileHeader}>
          {dismissible && (
            <IconButton
              aria-label={t('common.closePanel')}
              onClick={handleClose}
              color="inherit"
              sx={{
                position: 'absolute',
                top: 0,
                right: theme.spacing(-1),
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      }
      onSpringEnd={handleSpringEnd}
      onSpringStart={handleSpringStart}
      // Locks maxHeight in cases when orientation changes, preventing
      // changes on snap points and interactions issues
      maxHeight={window.innerHeight}
      aria-label={t('geoEvent.details')}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          // Add padding to the bottom to account for the overlapping features control (search card min snap height)
          pb: hasOverlappingFeatures
            ? `${getSearchCardMinSnapPx()}px`
            : undefined,
        }}
      >
        <Card
          className={classes.card}
          sx={{
            overflow: 'unset',
            maxHeight: 'unset',
            bgcolor: 'background.paper',
          }}
          elevation={0}
        >
          <CardContent sx={{ padding: 0 }}>
            <MobileScrollProvider
              scrollElementRef={scrollElementRef}
              closeDrawer={handleClose}
            >
              <WithCustomKey customKey={entityId}>{children}</WithCustomKey>
            </MobileScrollProvider>
          </CardContent>
        </Card>
      </Box>
    </BottomSheet>
  );
};
